// src/modules/coding-opportunities3/clinical-notes/AiCodingClinicalNotePage.jsx
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Divider,
  Stack,
  Chip,
  Button,
  Alert,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";

// ----------------------- helpers UI -----------------------
const clamp = (s, n = 160) => {
  const t = String(s || "");
  if (t.length <= n) return t;
  return t.slice(0, n).trim() + "…";
};

const scoreColor = (v) => {
  if (v === null || v === undefined) return "default";
  if (v >= 0.85) return "success";
  if (v >= 0.65) return "warning";
  return "default";
};

const decisionColor = (d) => {
  const x = String(d || "").toLowerCase();
  if (x.includes("approve") || x.includes("accept")) return "success";
  if (x.includes("reject")) return "error";
  if (x.includes("review")) return "warning";
  return "default";
};

export default function AiCodingClinicalNotePage() {
  // Hooks ALWAYS at the top (never after a return)
  const location = useLocation();
  const navigate = useNavigate();

  // Read state without breaking hooks
  const state = location?.state ?? null;

  // Editing state for fields (optional)
  const [editing, setEditing] = useState(true);
  const [chiefComplaint, setChiefComplaint] = useState("");
  const [assessmentPlan, setAssessmentPlan] = useState("");

  // Safe derived values (use defaults if no state)
  const patient = useMemo(() => state?.patient ?? null, [state]);
  const notesSummary = useMemo(() => state?.notesSummary ?? null, [state]);
  const opsAnalysis = useMemo(() => state?.opsAnalysis ?? null, [state]);

  const analyzedOps = useMemo(() => opsAnalysis?.opportunities ?? [], [opsAnalysis]);

  const patientName = patient?.memberFullName || patient?.name || "—";
  const cipId = patient?.cipId || "—";

  // Auto-prefill (no if before hooks)
  const autoSummaryText = useMemo(() => {
    const overall =
      opsAnalysis?.overall_summary ||
      opsAnalysis?.patient_summary ||
      notesSummary?.notesSummary?.summary ||
      "";

    const top = analyzedOps
      .slice(0, 8)
      .map((op) => {
        const code = op?.code || op?.diagnosisCode || "—";
        const decision = op?.decision || "—";
        const conf = Math.round((Number(op?.confidence || 0) * 100)) + "%";
        const rationale = clamp(op?.rationale, 140);
        return `• ${code} — ${decision} (${conf}): ${rationale}`;
      })
      .join("\n");

    return [overall, top].filter(Boolean).join("\n\n");
  }, [opsAnalysis, notesSummary, analyzedOps]);

  // Optionally initialize text fields once when state exists (without breaking hooks)
  // Note: to keep it simple without useEffect, we just set defaults when showing.
  const hasState = !!state;

  // ----------------------- RENDER -----------------------
  if (!hasState) {
    // This return is safe because there are no hooks below
    return (
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Alert severity="warning" sx={{ mb: 1 }}>
          No AI payload found. Please run AI Assist and click “Link result to Clinical Note”.
        </Alert>
        <Button variant="outlined" onClick={() => navigate("/coding-opportunities")}>
          Back to Coding Opportunities
        </Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h6" fontWeight={900}>
            AI Coding Clinical Note
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>
            Patient: <b>{patientName}</b> • CIP: <b>{cipId}</b> • Opportunities:{" "}
            <b>{analyzedOps.length}</b>
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Back
          </Button>
          <Button
            variant={editing ? "contained" : "outlined"}
            onClick={() => setEditing((v) => !v)}
          >
            {editing ? "Editing: ON" : "Editing: OFF"}
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        {/* LEFT: Prefill fields */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography fontWeight={900} sx={{ mb: 1 }}>
              Chief Complaint / HPI (prefilled)
            </Typography>

            <textarea
              rows={10}
              style={{ width: "100%", padding: 10, borderRadius: 10 }}
              disabled={!editing}
              value={chiefComplaint || autoSummaryText}
              onChange={(e) => setChiefComplaint(e.target.value)}
            />

            <Divider sx={{ my: 2 }} />

            <Typography fontWeight={900} sx={{ mb: 1 }}>
              Assessment / Plan (prefilled)
            </Typography>

            <textarea
              rows={10}
              style={{ width: "100%", padding: 10, borderRadius: 10 }}
              disabled={!editing}
              value={assessmentPlan || autoSummaryText}
              onChange={(e) => setAssessmentPlan(e.target.value)}
            />
          </Paper>
        </Grid>

        {/* RIGHT: Table summary */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography fontWeight={900}>Coding Opportunities (AI Result)</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.75 }}>
              {opsAnalysis?.overall_summary || opsAnalysis?.patient_summary || "—"}
            </Typography>

            <Divider sx={{ my: 1.5 }} />

            {analyzedOps.length === 0 ? (
              <Alert severity="info">No opportunities in the analysis payload.</Alert>
            ) : (
              <Box sx={{ maxHeight: 520, overflow: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Decision</TableCell>
                      <TableCell>Confidence</TableCell>
                      <TableCell>Rationale</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analyzedOps.map((op, idx) => (
                      <TableRow key={op?.id ?? idx} hover>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <b>{op?.code || op?.diagnosisCode || "—"}</b>
                          {op?.hcc ? (
                            <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>
                              HCC: {op.hcc}
                            </Typography>
                          ) : null}
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={op?.decision || "—"}
                            color={decisionColor(op?.decision)}
                          />
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={`${Math.round((Number(op?.confidence || 0) * 100))}%`}
                            color={scoreColor(Number(op?.confidence || 0))}
                            variant="outlined"
                          />
                        </TableCell>

                        <TableCell sx={{ maxWidth: 360 }}>
                          <Typography variant="body2">{clamp(op?.rationale, 180) || "—"}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Connect your real "Save Note" functionality here */}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => alert("TODO: wire Save Draft")}>
                Save Draft
              </Button>
              <Button variant="contained" onClick={() => alert("TODO: wire Submit Note")}>
                Create Note
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
}
